import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { Megaphone, LinkSimple, ChartLineUp, CurrencyDollar, Copy, CheckCircle, CircleNotch, Clock } from "@phosphor-icons/react";
import { Header } from "@/components/Header";
import { API } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { tierStyle, fmtNum, fmtRate } from "@/components/TierBadge";

const STEPS = [
  { icon: Megaphone, title: "Apply", text: "Pick your handle and tell us where your audience hangs out. We review every application by hand." },
  { icon: LinkSimple, title: "Share your link", text: "Once approved, drop your /?ref=handle link in videos, bios, streams and pods." },
  { icon: ChartLineUp, title: "Get credit", text: "Every visitor you send is yours for 30 days — first click wins, return visits count." },
];

const TierLadder = ({ tiers }) => (
  <div data-testid="partner-tiers" className="mt-8 grid gap-3 sm:grid-cols-3">
    {tiers.map((t, i) => (
      <div
        key={t.name}
        data-testid={`partner-tier-${i + 1}`}
        className="relative border border-white/10 bg-[#0A0A0A] p-5 rv-fade"
        style={{ animationDelay: `${0.08 * i}s` }}
      >
        <span className={`inline-block border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] ${tierStyle(i + 1)}`}>{t.name}</span>
        <p className="mt-3 font-serif text-3xl text-[#EDEDED]">${fmtRate(t.rate_per_1000)}<span className="text-sm text-white/40"> / 1k</span></p>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-white/45">
          {t.min_uniques === 0 ? "From your first visitor" : `Unlocked at ${fmtNum(t.min_uniques)} visitors`}
        </p>
      </div>
    ))}
  </div>
);

const field = "w-full border border-white/10 bg-[#0A0A0A] px-4 py-3 text-sm text-white placeholder-white/25 focus:border-amber focus:outline-none transition-colors duration-300";
const label = "block font-mono text-[10px] uppercase tracking-[0.2em] text-white/45 mb-2";

const useHandleCheck = (handle) => {
  const [state, setState] = useState(null);
  useEffect(() => {
    if (handle.length < 3) return setState(null);
    setState({ checking: true });
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/partners/check/${encodeURIComponent(handle)}`);
        setState(data);
      } catch {
        setState(null);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [handle]);
  return state;
};

const SignupDone = ({ handle }) => {
  const link = `${window.location.origin}/api/r/${handle}`;
  const copy = () => navigator.clipboard?.writeText(link).then(() => toast.success("Link copied"));
  return (
    <div data-testid="partner-signup-success" className="border border-emerald-400/30 bg-emerald-400/[0.05] p-6 sm:p-8">
      <CheckCircle size={32} weight="fill" className="text-emerald-300" />
      <h3 className="mt-4 font-serif text-2xl text-[#EDEDED]">Application received</h3>
      <p className="mt-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-amber">
        <Clock size={14} weight="bold" /> Pending review
      </p>
      <p className="mt-4 text-sm text-white/55 leading-relaxed">
        Your link starts earning credit the moment we approve you. Save it now:
      </p>
      <div className="mt-4 flex items-stretch gap-2">
        <code data-testid="partner-ref-link" className="flex-1 truncate border border-white/10 bg-black px-4 py-3 font-mono text-xs text-white/80">{link}</code>
        <button data-testid="partner-copy-link" onClick={copy} className="flex items-center gap-2 border border-amber/50 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-amber hover:bg-amber hover:text-black transition-colors duration-300">
          <Copy size={14} /> Copy
        </button>
      </div>
      <Link to="/partners/stats" data-testid="partner-stats-link" className="mt-5 inline-block font-mono text-[11px] uppercase tracking-[0.15em] text-white/50 hover:text-amber transition-colors">
        Check your stats →
      </Link>
    </div>
  );
};

const SignupForm = ({ onDone }) => {
  const [form, setForm] = useState({ promoter_id: "", name: "", email: "", channel_url: "", audience: "" });
  const [busy, setBusy] = useState(false);
  const handle = form.promoter_id.trim().toLowerCase();
  const check = useHandleCheck(handle);
  const set = (k) => (e) => setForm({ ...form, [k]: k === "promoter_id" ? e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/partners/signup`, { ...form, promoter_id: handle });
      onDone(data.promoter_id);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? "Please check your email and fields." : d || "Sign-up failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form data-testid="partner-signup-form" onSubmit={submit} className="grid gap-5 border border-white/10 bg-[#0A0A0A]/60 p-6 sm:p-8">
      <div>
        <label className={label} htmlFor="p-handle">Your handle (your ?ref= ID)</label>
        <input id="p-handle" data-testid="partner-handle-input" required minLength={3} maxLength={30} value={form.promoter_id} onChange={set("promoter_id")} placeholder="retro_gamer" className={field} />
        <p data-testid="partner-handle-status" className="mt-2 min-h-[16px] font-mono text-[11px]">
          {check?.checking && <span className="text-white/35">Checking…</span>}
          {check && !check.checking && (check.available
            ? <span className="text-emerald-300">✓ {window.location.host}/?ref={handle} is available</span>
            : <span className="text-crimson">{check.reason}</span>)}
        </p>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={label} htmlFor="p-name">Name</label>
          <input id="p-name" data-testid="partner-name-input" required maxLength={80} value={form.name} onChange={set("name")} className={field} />
        </div>
        <div>
          <label className={label} htmlFor="p-email">Email</label>
          <input id="p-email" data-testid="partner-email-input" type="email" required value={form.email} onChange={set("email")} className={field} />
        </div>
      </div>
      <div>
        <label className={label} htmlFor="p-channel">Channel / profile link</label>
        <input id="p-channel" data-testid="partner-channel-input" type="url" maxLength={300} value={form.channel_url} onChange={set("channel_url")} placeholder="https://youtube.com/@you" className={field} />
      </div>
      <div>
        <label className={label} htmlFor="p-aud">Your audience (optional)</label>
        <textarea id="p-aud" data-testid="partner-audience-input" rows={3} maxLength={500} value={form.audience} onChange={set("audience")} placeholder="Retro gaming streams, ~5k weekly viewers" className={field} />
      </div>
      <button
        data-testid="partner-signup-submit"
        type="submit"
        disabled={busy || (check && !check.checking && !check.available)}
        className="rv-cta-glow flex items-center justify-center gap-2 bg-amber px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-black disabled:opacity-40"
      >
        {busy ? <CircleNotch size={16} className="animate-spin" /> : <Megaphone size={16} weight="fill" />} Apply to become a partner
      </button>
    </form>
  );
};

export default function Partners() {
  const [tiers, setTiers] = useState([]);
  const [done, setDone] = useState(null);
  useEffect(() => {
    axios.get(`${API}/partners/program`).then(({ data }) => setTiers(data.tiers || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>ReelVault Partner Program | Earn $25-$40 per 1k Visitors</title>
        <meta name="description" content="Monetize your audience without selling products. Get paid to give away free entertainment. Lock in your custom tracking link today." />
      </Helmet>
      <Header />
      <main className="mx-auto max-w-[1200px] px-5 sm:px-8 py-16 sm:py-24 rv-fade">
        <div className="grid gap-14 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] lg:gap-20">
          <section>
            <span className="inline-flex items-center gap-2 border border-amber/50 bg-amber/10 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-amber">
              <Megaphone size={14} weight="duotone" /> Ambassador Program
            </span>
            <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#EDEDED] leading-[1.05]">
              Send viewers.<br /><span className="text-amber">Get paid.</span>
            </h1>
            <p className="mt-5 max-w-lg text-white/55 text-sm sm:text-base leading-relaxed">
              Gamers, podcasters, film nerds and curators — share ReelVault's free catalog with your audience and earn for every unique visitor you bring in — with higher rates as your traffic grows.
            </p>
            <div data-testid="partner-rate" className="mt-8 flex items-baseline gap-3 border-l-2 border-amber pl-5">
              <CurrencyDollar size={22} weight="fill" className="text-amber self-center" />
              <span className="font-serif text-2xl text-[#EDEDED]">Earn more as you grow</span>
            </div>
            <TierLadder tiers={tiers} />
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.12em] text-white/35 leading-relaxed">
              Unique visitors per calendar month · each band pays its own rate · resets on the 1st
            </p>
            <ol className="mt-14 grid gap-8">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <li key={s.title} className="flex gap-5 rv-fade" style={{ animationDelay: `${0.1 + i * 0.12}s` }}>
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-white/10 bg-[#0A0A0A]">
                      <Icon size={20} weight="duotone" className="text-amber" />
                    </span>
                    <div>
                      <h3 className="font-serif text-xl text-[#EDEDED]">{i + 1}. {s.title}</h3>
                      <p className="mt-1 text-sm text-white/50 leading-relaxed">{s.text}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
            <p className="mt-12 font-mono text-[10px] uppercase tracking-[0.15em] text-white/30 leading-relaxed max-w-md">
              30-day first-touch attribution · repeat refreshes within 30 minutes aren't double-counted · Kids hub traffic is ad-free and not monetized.
            </p>
          </section>
          <section>{done ? <SignupDone handle={done} /> : <SignupForm onDone={setDone} />}</section>
        </div>
      </main>
    </div>
  );
}
